import { SkyDialog, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import type { TenantView } from "@shared/types/tenants";
import { ErrorState } from "./ScreenState";

/**
 * Disable or re-enable a tenant (FR-20, canvas 10 panel 02; round 6 A1: one Status; round 6
 * T-04: the customer folded into its tenant, platform 0027).
 *
 * There is no delete, and the dialog says what disabling does in the terms the reader cares
 * about, and only what is true (round 6 A1, owner 2026-10-01: "make the dialog's promise
 * true"). Its people can no longer open its instances: `instanceContext`, the WebSocket upgrade
 * and the instance picker treat every instance of a disabled tenant as disabled
 * (`isInstanceDisabled`, `server/auth/accessResolver.ts`), so the app also refuses Run now
 * there. The runner skips its schedules too (round 7: the due scan leaves a disabled tenant's
 * instances out, `tenant_disabled` in `runner/scheduler/dueScan.ts`). The instance count is
 * named because "disable this tenant" reads as one row and is in fact a subtree.
 *
 * F32 (owner decision 2026-10-10, `docs/adr/0010-amendment-2026-10-10-disable.md`; the copy
 * must be true): disabling blocks the tenant in the app and revokes nothing in Azure, the same for
 * a shared tenant and one with its own resources. The runner cancels its runs in flight
 * ("Stopped because the tenant was disabled.", `tenant_disabled`,
 * `runner/tenancy/disabledTenantRuns.ts`), starts nothing for it and drops its customer alerts.
 * Enabling is instant: cancelled runs stay cancelled and schedules fire at their next time, with
 * no catch-up of the ones missed. A tenant's own Azure resources and their access stay as they are.
 *
 * The test id keeps its round 5 word (`customer-disable-dialog`): e2e specs read it.
 */
export function DisableTenantDialog({
  open,
  onOpenChange,
  tenant,
  onConfirm,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: TenantView | null;
  onConfirm: () => void;
  isSaving: boolean;
  error: unknown;
}) {
  if (!tenant) return null;
  const disabling = tenant.status !== "disabled";
  const instanceCount = tenant.instanceCount;
  const ownResources = tenant.resources !== undefined && tenant.resources.mode !== "shared";

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      // The tenant is named in the heading so a screen reader hears which one is about to
      // change, not just that something is.
      title={disabling ? `Disable ${tenant.displayName}` : `Enable ${tenant.displayName}`}
      description={
        disabling
          ? "Nothing is deleted. Nobody can open its instances, in the app or through its API, and the history stays where it is."
          : "Its people can open its instances again, and its schedules fire at their next time. Cancelled runs stay cancelled."
      }
      size="medium"
      testId="customer-disable-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a
      // form the server has just refused. The dialog closes when the mutation
      // succeeds, and only then; a failure leaves the typing where it is.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: disabling ? "Disable tenant" : "Enable tenant",
        onClick: onConfirm,
        loading: isSaving,
        destructive: disabling,
      }}
      secondaryAction={{
        label: disabling ? "Keep active" : "Cancel",
        onClick: () => onOpenChange(false),
      }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That change was not saved" error={error} /> : null}

        {disabling ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title={`Its ${instanceCount} ${instanceCount === 1 ? "instance closes" : "instances close"} with it`}
            message="Running imports and transformations in this tenant are cancelled, and nobody can start one. Its alerts stop. Schedules stop firing until the tenant is enabled again, then fire at their next time."
          />
        ) : null}

        {disabling && ownResources ? (
          <SkyText as="p" size={300} block testId="tenant-disable-access">
            Its stored data and settings are kept, so enabling it again is instant.
          </SkyText>
        ) : null}

        <SkyText as="p" size={200} color="muted" block>
          The change is recorded on the audit trail with your name and the reference id of this
          request.
        </SkyText>
      </div>
    </SkyDialog>
  );
}
